import { Flex, Grid, GridItem, Image } from '@chakra-ui/react';
import styled from '@emotion/styled';
import { useSelector } from 'react-redux';
import { ReviewActionButton } from 'screens/home/summary/components/Cards/Reviews/ReviewActionButton';
import { useMySummaryReviews } from 'screens/home/summary/components/Cards/Reviews/hooks';
import { useOneOnOneInstanceDetails } from 'screens/oneOnOneRabbit/InstancePage/hooks/dataHooks';
import useOneOnOnePageParams from 'screens/oneOnOneRabbit/hooks/useOneOnOnePageParams';
import { selectLoggedInEmployeeId } from 'selectors/loggedInEmployee';

import { ReviewsIconDualTone } from 'assets/assetsV1/iconComponents';
import NoReviewsImage from 'assets/assetsV1/illustrations/noReviews.svg';

import { Avatar } from 'uiToolkitV1/Avatar';
import { DateComp } from 'uiToolkitV1/DateComp';
import { SkeletonLoader } from 'uiToolkitV1/SkeletonLoader';
import { TextBodyMedium, TextLabel } from 'uiToolkitV1/Typography';

import { SidebarCardTemplate } from '../common/components/SidebarCardTemplate';

// /* @ts-ignore */
const Container = styled(Flex)`
  padding: 24px 20px;
  position: relative;
  &:hover {
    background: #f6fafe;
    & .highlightTextOnHover {
      color: #1565bc;
    }
  }
  cursor: pointer;
  background: #ffffff;
`;

const ButtonContainer = styled(Flex)`
  height: 100%;
  visibility: hidden;
  align-items: center;
  position: absolute;
  top: -30px;
  right: 8px;
  & .action {
    margin-left: auto;
  }
  ${Container}:hover & {
    visibility: visible;
  }
`;

const ReviewsMySummaryRow = ({ employee, review, nominationsCount, publishedOn }) => {
  const { displayName, profilePhoto } = employee ?? {};
  const reviewCycleName = review?.feedbackForm?.reviewCycle?.name;
  return (
    <Container flexDir="column" mb="8px" borderRadius="4px">
      <Grid templateColumns="repeat(6, 1fr)" width="100%" gap={2}>
        <GridItem colSpan={5}>
          <Flex flexDir="column">
            <TextLabel color="text.g2" mb="4px">
              Review for
            </TextLabel>
            <ButtonContainer className="showOnHover">
              <ReviewActionButton review={review} nominationsCount={nominationsCount} />
            </ButtonContainer>
          </Flex>
          <Flex alignItems="center" sx={{ marginBottom: '8px' }}>
            {/* @ts-expect-error invalid size */}
            <Avatar size={24} src={profilePhoto} name={displayName} />
            <TextBodyMedium color="text.g1" ml="8px" className="highlightTextOnHover">
              {displayName}
            </TextBodyMedium>
          </Flex>
          <Flex flexDir="column" className="hideTextOnHover">
            <TextBodyMedium color="text.g2">Review Cycle: {reviewCycleName}</TextBodyMedium>
            {publishedOn ? (
              <Flex
                sx={{
                  p: {
                    color: '#666666',
                  },
                }}
              >
                <TextBodyMedium color="text.g2">
                  Published on:<>&nbsp;</>
                </TextBodyMedium>
                <DateComp
                  date={publishedOn}
                  isRelativeDateFormat
                  enableRelativeFormatDistanceColor
                  // textComponent={TextBodyMedium}
                  forceRelativeDateFormat
                  showDateInBrackets
                />
              </Flex>
            ) : null}
          </Flex>
        </GridItem>
        <GridItem colStart={6} colEnd={7} />
      </Grid>
    </Container>
  );
};

const ReviewsSection = () => {
  const { masterId, instanceId } = useOneOnOnePageParams();

  const {
    receiverEmployee: { id: employeeId, displayName },
  } = useOneOnOneInstanceDetails({
    masterId,
    instanceId,
  });

  const loggedInEmployeeId = useSelector(selectLoggedInEmployeeId);

  const isLoggedInEmployeeTheReceiver = loggedInEmployeeId === employeeId;

  const { data, isLoading } = useMySummaryReviews({
    limit: 50,
    offset: 0,
    sectionId: 'MY_PAST_REVIEWS',
    employeeId,
    keepPreviousData: true,
    refetchOnWindowFocus: false,
    includeCount: true,
  });

  if (isLoading) {
    return <SkeletonLoader />;
  }

  if (!data?.data?.length) {
    return (
      <SidebarCardTemplate label="Reviews" Icon={ReviewsIconDualTone}>
        <Flex mt="24px" flexDir="column" alignItems="center" justifyContent="center">
          <Image width="172px" src={NoReviewsImage as unknown as string} />
          <Flex w="245px" textAlign="center" mt="22px" flexDir="column" p="12px">
            <TextBodyMedium bold>
              {isLoggedInEmployeeTheReceiver
                ? "You don't have any reviews yet"
                : `There are no reviews for ${displayName}`}
            </TextBodyMedium>
            <TextBodyMedium color="text.g2">
              {isLoggedInEmployeeTheReceiver
                ? "Looks like you haven't completed any review cycle yet"
                : `Looks like ${displayName} hasn't completed any review cycle yet`}
            </TextBodyMedium>
          </Flex>
        </Flex>
      </SidebarCardTemplate>
    );
  }

  return (
    <SidebarCardTemplate label="Reviews" Icon={ReviewsIconDualTone}>
      {data?.data?.map(({ employee, deadline, review, nominationsCount, publishedOn }, index) => (
        <ReviewsMySummaryRow
          employee={employee}
          review={review}
          // eslint-disable-next-line react/no-array-index-key
          key={`${employee?.displayName}-${index}-review-row`}
          nominationsCount={nominationsCount}
          publishedOn={publishedOn}
        />
      ))}
    </SidebarCardTemplate>
  );
};

export { ReviewsSection };
